import { FormContainer, notify, Text, useConfirmation } from '@wyz/ui';
import { invariant, useModalState } from '@wyz/utils';
import { isNil, pick } from 'lodash-es';
import { useForm } from 'react-hook-form';
import { Trans, useI18n } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { useManageQuoteCart, useManageSearch } from '~/purchase/api';
import {
  QuoteDetailModal,
  QuoteRowItem,
  QuoteStandardItem,
} from '~/purchase/components';
import { QuoteHelpers } from '~/purchase/helpers';
import { useQuoteCart } from '~/purchase/hooks';
import {
  IBaseTyreOffer,
  IProductListState,
  IQuoteAddToCartForm,
  IQuoteTyreOffer,
  ISearchTypeArgs,
} from '~/purchase/types';
import clsx from 'clsx';
import { useCallback, useRef } from 'react';

type Props = {
  tyreOffer: IQuoteTyreOffer;
  pointOfSaleView?: boolean;
  defaultQuantity: number;
  customer: IUserCustomer;
  showBfDiscount: boolean;
  type?: 'CARD' | 'LINE';
  searchType: ISearchTypeArgs['searchType'];
  searchQuery: IProductListState['searchQuery'];
  onUpdateQuery: (tyreOffer: IBaseTyreOffer) => IBaseTyreOffer | undefined;
};

function ProductResult({
  tyreOffer,
  defaultQuantity,
  pointOfSaleView = false,
  customer,
  showBfDiscount,
  searchType,
  searchQuery,
  onUpdateQuery,
  type = 'CARD',
}: Props) {
  const { t } = useI18n();
  const ask = useConfirmation();
  const methods = useForm<IQuoteAddToCartForm>({
    defaultValues: { quantity: defaultQuantity },
  });
  const { actions } = useQuoteCart();
  const manageCart = useManageQuoteCart({
    get: { enabled: true },
  });

  const manageSearch = useManageSearch<IQuoteTyreOffer>({
    context: {
      customerId: customer.id,
      searchType,
    },
    refreshEan: { enabled: true },
  });

  const [modal, modalHandlers] = useModalState<'DETAIL'>();
  // Last quantity the displayed offer was priced for, restored when a new quantity yields no offer.
  const offerQuantityRef = useRef(defaultQuantity);
  const quoteItemInfo = QuoteHelpers.getItemInfoFromTyreOffer(tyreOffer);

  const handleAddToCart = (quantity: number) => {
    invariant(manageCart.get.isSuccess);
    const cart = manageCart.get.data.data;
    if (
      cart.dimensionCount === QuoteHelpers.QUOTE_CART_MAX_DIMENSION_COUNT ||
      cart.referenceCount >= QuoteHelpers.QUOTE_CART_MAX_REFERENCE_COUNT ||
      (cart.referenceCount > 1 &&
        !cart.items.some((item) =>
          QuoteHelpers.isSameDimension(item, quoteItemInfo.tyre),
        ))
    ) {
      ask({
        title: t('app.purchase.quote_cart_page.messages.cannot_add_dimension'),
        confirmBtnText: t('app.purchase.cart_page.reset_cart'),
        cancelBtnText: t('app.common.button.cancel'),
      }).onConfirm(() => actions.reset.process());
      return;
    } else if (
      cart.dimensionCount === 1 &&
      !cart.items.some((item) =>
        QuoteHelpers.isSameDimension(item, quoteItemInfo.tyre),
      )
    ) {
      ask({
        title: t(
          'app.purchase.quote_cart_page.messages.different_dimension.title',
        ),
        description: t(
          'app.purchase.quote_cart_page.messages.different_dimension.description',
        ),
        confirmBtnText: t('app.purchase.cart_page.reset_cart'),
        cancelBtnText: t('app.purchase.quote_search_page.add_to_quote'),
      })
        .onConfirm(() => actions.reset.process())
        .onCancel(() => {
          invariant(quoteItemInfo.pricing);
          const payload = {
            ...pick(quoteItemInfo, ['tyre', 'stocks']),
            buyingPrice: quoteItemInfo.pricing.buyingPrice,
            unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
            vatPercentage: quoteItemInfo.pricing.vatPercentage,
            offerType: quoteItemInfo.pricing.offerType,
            supplierName: quoteItemInfo.pricing.supplierName,
            deliveryDate: quoteItemInfo.pricing.deliveryDate,
            quantity,
            currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
          };
          actions.addItem.process(payload);
        });
    } else {
      invariant(quoteItemInfo.pricing);
      const payload = {
        ...pick(quoteItemInfo, ['tyre', 'stocks', 'proximityStocks']),
        buyingPrice: quoteItemInfo.pricing.buyingPrice,
        unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
        vatPercentage: quoteItemInfo.pricing.vatPercentage,
        recyclingFee: quoteItemInfo.pricing.recyclingFee,
        offerType: quoteItemInfo.pricing.offerType,
        supplierName: quoteItemInfo.pricing.supplierName,
        deliveryDate: quoteItemInfo.pricing.deliveryDate,
        quantity,
        currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
      };
      actions.addItem.process(payload);
    }
  };

  const handleOnRefreshTyre = useCallback(
    (ean: string, newQuantity: number, forceNoLatency: boolean = false) => {
      if (isNil(searchQuery)) {
        return;
      }
      manageSearch.refreshEan.mutate(
        {
          ean: ean,
          baseQuery: {
            ...searchQuery,
            quantity: newQuantity,
          },
          forceNoLatency,
        },
        {
          onSuccess: (tyreOffer) => {
            if (isNil(searchQuery)) {
              return;
            }
            // The reference can drop out of the results for the new quantity (e.g. only available
            // on order while the purchase module is off): keep the displayed offer consistent with
            // the quantity instead of quoting the new quantity at the old offer's price.
            if (isNil(tyreOffer)) {
              methods.setValue('quantity', offerQuantityRef.current);
              notify.warning(
                <Text id={`quantity-unavailable-warning-${ean}`}>
                  <Trans i18nKey='app.purchase.quote_search_page.product.quantity_unavailable' />
                </Text>,
                { id: 'toast-warning-quote-quantity-unavailable' },
              );
              return;
            }
            offerQuantityRef.current = newQuantity;
            onUpdateQuery(tyreOffer);
          },
        },
      );
    },
    [manageSearch.refreshEan, methods, onUpdateQuery, searchQuery],
  );

  return (
    <FormContainer
      methods={methods}
      submitHandler={({ quantity }) => {
        handleAddToCart(quantity);
      }}
      className={clsx('position-relative', { 'h-100': type === 'CARD' })}
    >
      {type === 'CARD' ? (
        <QuoteStandardItem
          quoteItemInfo={quoteItemInfo}
          customer={customer}
          showBfDiscount={showBfDiscount}
          pointOfSaleView={pointOfSaleView}
          tyreOffer={tyreOffer}
          onRefreshTyre={handleOnRefreshTyre}
          isRefreshingTyre={manageSearch.refreshEan.isLoading}
          onShowDetails={() => modalHandlers.open('DETAIL')}
          addItemLoading={actions.addItem.isLoading}
        />
      ) : (
        <QuoteRowItem
          quoteItemInfo={quoteItemInfo}
          tyreOffer={tyreOffer}
          onShowDetails={() => modalHandlers.open('DETAIL')}
          pointOfSaleView={pointOfSaleView}
          onRefreshTyre={handleOnRefreshTyre}
          isRefreshingTyre={manageSearch.refreshEan.isLoading}
          addItemLoading={actions.addItem.isLoading}
          customer={customer}
        />
      )}
      {modal === 'DETAIL' && (
        <QuoteDetailModal
          customer={customer}
          item={quoteItemInfo}
          onClose={() => modalHandlers.close()}
          currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
        />
      )}
      {manageSearch.refreshEan.isLoading && <div className='shimmer-overlay' />}
    </FormContainer>
  );
}
export default ProductResult;
